import { createContext, FC, useCallback, useContext, useRef } from "react";

import { baseTheme, ExportFormat, TableToolbar, TableToolbarSearch } from "@asd/customer-portal-ui";
import InfoOutlineIcon from "@mui/icons-material/InfoOutline";
import RestoreIcon from "@mui/icons-material/Restore";
import { Divider, IconButton, Stack, Tooltip } from "@mui/material";
import type { GridFilterModel, GridLogicOperator } from "@mui/x-data-grid/models";

import TableFilterPanel from "./TableFilterPanel";

export interface TableViewToolbarContextValue {
  searchTerm: string;
  filterModel: GridFilterModel;
  setFilterModel: React.Dispatch<React.SetStateAction<GridFilterModel>>;
  setPaginationModel: React.Dispatch<React.SetStateAction<{ pageSize: number; page: number }>>;
  setExpandedIds: React.Dispatch<React.SetStateAction<Set<unknown>>>;
  setAnimatingIds: React.Dispatch<React.SetStateAction<Set<unknown>>>;
  setSearchTerm: (term: string) => void;
  clearSearchTermRef: React.RefObject<(() => void) | null>;
  handleReset: () => void;
  handleExport: (format: ExportFormat) => void;
}

export const TableViewToolbarContext = createContext<TableViewToolbarContextValue>(null!);

const useDebouncedCallback = <T extends (...args: any[]) => void>(fn: T, delay: number): T => {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  return useCallback(
    ((...args) => {
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => fn(...args), delay);
    }) as T,
    [fn, delay],
  );
};

export const TableViewToolbar: FC = () => {
  const {
    searchTerm,
    filterModel,
    setFilterModel,
    setPaginationModel,
    setExpandedIds,
    setAnimatingIds,
    setSearchTerm,
    clearSearchTermRef,
    handleReset,
    handleExport,
  } = useContext(TableViewToolbarContext);

  const debouncedFilterItemsChange = useDebouncedCallback((items) => {
    setFilterModel((prev) => ({ ...prev, items }));
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    setExpandedIds(new Set());
    setAnimatingIds(new Set());
  }, 150);

  const debouncedLogicOperatorChange = useDebouncedCallback((op) => {
    setFilterModel((prev) => ({ ...prev, logicOperator: op }));
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, 150);

  return (
    <TableToolbar
      leftActions={[
        <Tooltip
          key="info"
          slotProps={{
            tooltip: {
              sx: {
                ...baseTheme.components.MuiTooltip.defaultProps.slotProps.tooltip.sx,
                maxWidth: 500,
              },
            },
          }}
          title="Rows with a strike-through indicate that the Service address is farther than Provider's willingness to travel"
        >
          <InfoOutlineIcon color="primary" sx={{ cursor: "help" }} />
        </Tooltip>,
        <Divider key="divider" orientation="vertical" sx={{ ml: 1 }} flexItem />,
        <Tooltip key="reset" sx={{ alignContent: "center", height: "fit-content" }} title="Reset">
          <IconButton onClick={handleReset} size="small">
            <RestoreIcon />
          </IconButton>
        </Tooltip>,
        <Stack key="search" alignItems="center" direction="row" ml={0.5} spacing={1}>
          <TableToolbarSearch
            clearCallbackRef={clearSearchTermRef}
            initialValue={searchTerm}
            onSearch={(term: string) => {
              setSearchTerm(term);
              setPaginationModel((prev) => ({ ...prev, page: 0 }));
              setExpandedIds(new Set());
              setAnimatingIds(new Set());
            }}
            keepExpanded
          />
          <TableFilterPanel
            filterItems={filterModel.items}
            logicOperator={filterModel.logicOperator ?? ("and" as GridLogicOperator)}
            onFilterItemsChange={debouncedFilterItemsChange}
            onLogicOperatorChange={debouncedLogicOperatorChange}
          />
        </Stack>,
      ]}
      onExport={handleExport}
    />
  );
};
